import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import $ from '@sparta.global.vendors/jquery';

/**
 * Utility global-search-common-utility
 * @description This utility is used for common functions and apis used by different components of global search.
 * @copyright 2021-7-16
 * @file
 */

/* global global-search-common-utility */

/**
 * The GlobalSearchCommonUtility.
 * This utility is used for common functions and apis used by different components of global search
 * @export
 * @class
 */
class GlobalSearchCommonUtility {
  /**
   * Creates an instance of GlobalSearchCommonUtility.
   * @memberof GlobalSearchCommonUtility
   */
  constructor() {
    this.entityRegExp = /&amp;([A-Za-z]+|#x[\dA-Fa-f]+|#\d+)(;?)/g;
    this.entityClean = {
      save: ['&trade;', '&reg;', '&Aacute;', '&aacute;', '&Eacute;', '&eacute;',
      // Spanish characters
      '&Iacute;', '&iacute;', '&Oacute;', '&oacute;', '&Uacute;', '&uacute;', '&Ntilde;', '&ntilde;', '&Uuml;', '&uuml;', '&iquest;', '&iexcl;', '&ordm;', '&ordf;', '&laquo;', '&raquo;', '&lsaquo;', '&rsaquo;'],
      ignore: ['<', '>']
    };
    this.html = {
      wrap(query, match, wrapper) {
        let i;
        const wrap = typeof wrapper !== 'undefined' && wrapper.indexOf('|') > -1 ? wrapper.split('|') : ['<b>', '</b>'];
        const emphasis = {
          open: {
            marker: '{{{{',
            wrap: wrap[0]
          },
          close: {
            marker: '}}}}',
            wrap: wrap[1]
          }
        };
        let matchRegex = new RegExp('');
        const cleanRegex = /<(?:.|\n)*?>/gm;
        let presentableQuery = objHelper.string.makePresentable(query.replace(cleanRegex, ''));
        const presentableMatch = objHelper.string.makePresentable(match.replace(cleanRegex, ''));
        const words = encodeURIComponent(presentableMatch).split('%20');
        const exclude = ['a', 'an', 'the', 'of', '.', '\\', ')', '(', '>', '<'];
        for (i = 0; i < words.length; i++) {
          if ($.inArray(words[i], exclude) > -1) {
            continue;
          }
          words[i] = words[i].replace(/\*|\(|\)/g, '.S+');
          matchRegex = new RegExp(`(${words[i]})`, 'gim');
          presentableQuery = presentableQuery.replace(matchRegex, `${emphasis.open.marker}$1${emphasis.close.marker}`);
        }
        presentableQuery = presentableQuery.split(emphasis.open.marker).join(emphasis.open.wrap).split(emphasis.close.marker).join(emphasis.close.wrap).replace(emphasis.open.wrap + emphasis.close.wrap, '');
        return presentableQuery;
      },
      injectIcons($container) {
        $('[data-icon]', $container).each(function () {
          const $object = $(this);
          const iconName = $object.data('icon');
          const iconClass = $.map(iconName.split(' '), a => {
            return `icon-${a}`;
          }).join(' ');
          const $icon = $('<div class="icon"/>').addClass(iconClass);
          const iconParts = 4;
          if (iconName === '#!') {
            return;
          }
          for (let i = 1; i <= iconParts; i++) {
            $icon.append(`<div class="icon-layer icon-layer--level${i}"/>`);
          }
          $object.removeData('icon').attr('data-icon', '#!');
          $object.append($icon);
        });
      },
      scrollTo(target, container, position, offset) {
        let method = 'position';
        let targetPosition = 0;
        if (target.length === 0) {
          return;
        }
        if (typeof container === 'undefined' || container === '' || container === null) {
          container = 'html, body';
        }
        if (container === 'html, body') {
          method = 'offset';
        }
        target = objHelper.jquerify(target);
        targetPosition = target.offset().top;
        offset = typeof offset === 'number' ? offset : 0;
        targetPosition = target[method]().top + offset;
        position = typeof position === 'string' ? position : 'top';
        if (position === 'bottom') {
          targetPosition += target.height();
        }
        $(container).animate({
          scrollTop: targetPosition
        }, 'slow');
      }
    };
    this.string = {
      makePresentable(string) {
        return $.trim(string).replace(/\s+/g, ' ');
      },
      makeComparable(string) {
        return objHelper.string.makePresentable(string).toLowerCase();
      },
      renderHtmlEntities(string) {
        while (objHelper.entityRegExp.test(string)) {
          string = string.replace(objHelper.entityRegExp, '&$1;');
        }
        return string;
      },
      entitiesToCharacters(text) {
        if (typeof text === 'undefined') {
          return '';
        }
        return $('<div/>').html(text).text();
      },
      charactersToEntities(text) {
        const save = objHelper.entityClean.save;
        const ignore = objHelper.entityClean.ignore;
        let matches = text.match(/([\u00A0-\u9999<>])/gim);
        // Remove any entities you want to keep.
        for (let i = 0; i < save.length; i++) {
          save[i] = objHelper.string.entitiesToCharacters(save[i]);
        }
        matches = $(matches).not(ignore).get();
        matches = $(matches).filter(save).get();
        if (matches.length === 0) {
          return text;
        }
        const re = new RegExp(matches.join('|'), 'gim');
        return text.replace(re, i => {
          return `&#${i.charCodeAt(0)};`;
        });
      },
      cleanString(string) {
        let i;
        let output = '';
        if (typeof string !== 'string') {
          return string;
        }
        string = objHelper.string.charactersToEntities(string);
        for (i = 0; i < string.length; i++) {
          if (string.charCodeAt(i) <= 127) {
            output += string.charAt(i);
          } else {
            output += ' ';
          }
        }
        return output;
      },
      repeat(value, times) {
        return new Array(times + 1).join(value);
      },
      formatAccounting(value, comma, point) {
        const tempComma = '||comma||';
        const tempPoint = '||point||';
        value = typeof value === 'number' ? value.toString() : value;
        comma = typeof comma !== 'string' ? ',' : comma;
        point = typeof point !== 'string' ? '.' : point;
        return value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,').replace(/\,/g, tempComma).replace(/\./g, tempPoint).split(tempComma).join(comma).split(tempPoint).join(point);
      },
      formatDecimal(value, depth) {
        const conversion = parseFloat(value.replace(/\,/g, ''));
        let shifted;
        let split;
        depth = typeof depth !== 'number' ? 2 : depth;
        if (conversion === 0) {
          return `0.${this.repeat('0', depth)}`;
        }
        shifted = Math.floor(conversion * Math.pow(10, depth)).toString();
        split = shifted.length - depth;
        if (split <= 0) {
          shifted = this.repeat('0', split <= 0 ? 1 - split : '') + shifted;
          split = shifted.length - depth;
        }
        return `${shifted.substring(0, split)}.${shifted.substring(split)}`;
      },
      /* Define function for escaping user input to be treated as 
         a literal string within a regular expression */
      escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      },
      /* Define functin to find and replace specified term with replacement string */
      replaceAll(str, term, replacement) {
        return str.replace(new RegExp(objHelper.string.escapeRegExp(term), 'g'), replacement);
      },
      // We use native security practices to prevent attacks, so we have to circumvent those...
      insecure: function () {
        const forbiddenCharacters = /[<>&*?%$@+!~()'";\s]/g;
        function encode(value) {
          const hex = value.charCodeAt(0).toString(16);
          return `[[${`0000${hex}`.slice(hex.length)}]]`;
        }
        return function (query) {
          return query ? query.replace(forbiddenCharacters, encode) : '';
        };
      }()
    };
    this.number = {
      romanize(num) {
        if (!+num) {
          return false;
        }
        const digits = String(+num).split(' ');
        const key = ['', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM', '', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC', '', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];
        let roman = '';
        let i = 3;
        while (i--) {
          roman = (key[+digits.pop() + i * 10] || '') + roman;
        }
        return Array(+digits.join('') + 1).join('M') + roman;
      }
    };
    this.lang = {
      DEFAULT: 'en',
      locales: {
        en: 'en_US',
        es: 'es_US'
      },
      normalize(locale) {
        const result = /^[a-z]+/i.exec(locale);
        if (result === null) {
          return objHelper.lang.DEFAULT;
        }
        const language = result[0].toLowerCase();
        return objHelper.lang.locales.hasOwnProperty(language) ? language : objHelper.lang.DEFAULT;
      },
      getPageLanguage() {
        return spartaGlobalFunctions.getLanguage();
      },
      get($link) {
        const lang = $link ? $link.data('language') : '';
        if (typeof lang !== 'string' || lang.length === 0) {
          return objHelper.lang.getPageLanguage();
        }
        return objHelper.lang.normalize(lang);
      },
      shouldDisplayOol($link) {
        const pageLanguage = objHelper.lang.getPageLanguage();
        // If the language on the current link doesn't match the page, we show the OOL (1).
        // For now we do not show an OOL to English-speaking (default) visitors (2).
        // And the flexModal must exist (3).
        return pageLanguage !== objHelper.lang.get($link) && pageLanguage !== objHelper.lang.DEFAULT && $.fn.boaFlexModal !== null;
      }
    };
    this.site = {
      getCurrentSite() {
        // Logic to identify site name based on fqdn will be implemented in future if need arises.
        // For now OLB is the default value returned by this method.
        return 'OLB';
      }
    };
  }
  jquerify(item) {
    return typeof item === 'string' ? $(item) : item;
  }
  queueMacroTask(callback) {
    setTimeout(callback, 0);
  }
  queueMicroTask(callback) {
    Promise.resolve(true).then(() => {
      callback();
    });
  }
}
const objHelper = new GlobalSearchCommonUtility();

export { objHelper as default };
